import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, effect } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppStateService } from 'src/app/shared/services/helpers/app-state.service';
import { ChannelSequenceComponent } from './sub-components/channel-sequence/channel-sequence.component';
import { ChannelFilterTimingComponent } from './sub-components/channel-filter-timing/channel-filter-timing.component';
import { ChannelBlacklistComponent } from './sub-components/channel-blacklist/channel-blacklist.component';
import { ChannelUnsubscribeComponent } from './sub-components/channel-unsubscribe/channel-unsubscribe.component';
import { ChannelTemplatesComponent } from './sub-components/channel-templates/channel-templates.component';
import {
  CHANNEL_SETUP_TABS,
  ChannelSetupTab,
  canSeeChannelSetupTab,
  canWriteChannelSetupTab,
  channelSetupAdminRules,
  isChannelSetupFullAdmin,
} from './channel-setup-access';

@Component({
  selector: 'app-project-channel-settings-home',
  templateUrl: './project-channel-settings-home.component.html',
  styleUrls: ['./project-channel-settings-home.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ChannelSequenceComponent,
    ChannelFilterTimingComponent,
    ChannelBlacklistComponent,
    ChannelUnsubscribeComponent,
    ChannelTemplatesComponent,
  ],
})
export class ProjectChannelSettingsHomeComponent implements OnInit, OnDestroy {
  activeTab: ChannelSetupTab = 'sequence';
  guidingBackup = false;
  private requestedTab: ChannelSetupTab | '' = '';
  private guideTimer: ReturnType<typeof setTimeout> | null = null;
  tabVisibility: Record<ChannelSetupTab, boolean> = {
    sequence: false,
    backup: false,
    filters: false,
    blacklist: false,
    unsubscribe: false,
    templates: false,
  };
  tabWrite: Record<ChannelSetupTab, boolean> = {
    sequence: false,
    backup: false,
    filters: false,
    blacklist: false,
    unsubscribe: false,
    templates: false,
  };

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly appStateService: AppStateService,
    private readonly changeDetector: ChangeDetectorRef,
  ) {
    effect(() => {
      if (!this.appStateService.initialized()) {
        return;
      }
      this.applyAdminRules();
      this.applyRequestedTab();
      this.changeDetector.markForCheck();
    });
  }

  ngOnInit(): void {
    this.applyAdminRules();
    this.requestedTab = this.parseTabParam(this.route.snapshot.queryParamMap.get('active_tab'));
    this.applyRequestedTab();
    this.route.queryParams.subscribe((params) => {
      this.requestedTab = this.parseTabParam(params['active_tab']);
      this.applyRequestedTab();
      this.changeDetector.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.clearGuideTimer();
  }

  startBackupSetupGuide(): void {
    this.applyAdminRules();
    if (!this.tabVisibility.backup) {
      return;
    }
    this.guidingBackup = true;
    this.changeDetector.markForCheck();
    this.clearGuideTimer();
    this.guideTimer = setTimeout(() => {
      this.toggleTab('backup');
      this.guideTimer = setTimeout(() => {
        this.guidingBackup = false;
        this.guideTimer = null;
        this.changeDetector.markForCheck();
      }, 1200);
      this.changeDetector.markForCheck();
    }, 1800);
  }

  toggleTab(tab: ChannelSetupTab): void {
    if (!this.tabVisibility[tab]) {
      return;
    }
    if (this.guidingBackup && tab !== 'backup') {
      this.clearGuideTimer();
      this.guidingBackup = false;
    }
    this.requestedTab = tab;
    this.activeTab = tab;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { active_tab: tab },
      queryParamsHandling: 'merge',
    });
  }

  private parseTabParam(value: unknown): ChannelSetupTab | '' {
    let requested = String(value || '') as ChannelSetupTab | 'on_demand';
    if (requested === 'on_demand') {
      requested = 'backup';
    }
    return CHANNEL_SETUP_TABS.includes(requested as ChannelSetupTab) ? requested as ChannelSetupTab : '';
  }

  private clearGuideTimer(): void {
    if (this.guideTimer) {
      clearTimeout(this.guideTimer);
      this.guideTimer = null;
    }
  }

  private applyAdminRules(): void {
    const adminRulesDic = channelSetupAdminRules();
    const authDetails: any = this.appStateService.managerAccess();
    const isFullAdmin = isChannelSetupFullAdmin(authDetails);
    CHANNEL_SETUP_TABS.forEach((tab) => {
      this.tabVisibility[tab] = canSeeChannelSetupTab(tab, adminRulesDic, isFullAdmin);
      this.tabWrite[tab] = canWriteChannelSetupTab(tab, adminRulesDic);
    });
    if (!this.isOnDemandTriggerEnabled()) {
      this.tabVisibility.backup = false;
      this.tabWrite.backup = false;
    }
  }

  private isOnDemandTriggerEnabled(): boolean {
    const brand: any = this.appStateService.brandDetails();
    return Boolean(
      brand?.module_configuration?.feedback?.submodules?.requests?.submodules?.is_on_demand_trigger_enabled?.visible,
    );
  }

  private applyRequestedTab(): void {
    if (this.requestedTab && this.tabVisibility[this.requestedTab]) {
      this.activeTab = this.requestedTab;
      return;
    }
    if (this.tabVisibility[this.activeTab]) {
      return;
    }
    this.activeTab = CHANNEL_SETUP_TABS.find((tab) => this.tabVisibility[tab]) || 'sequence';
  }
}
